import firebase from "firebase/compat/app";
import "firebase/compat/messaging";
import { firebaseConfig, firebaseVapidKey } from "../config/firebaseConfig";
import paths from "../utils/constants";

if (!firebase.apps.length) {
  firebase.initializeApp(firebaseConfig);
}

const SW_PATH = "/firebase-messaging-sw.js";
const FCM_TOKEN_STORAGE_KEY = "fcm_token";
const NOTIFICATION_ICON = "/mainlogo.png";

function resolveNotificationUrl(path) {
  if (typeof window === "undefined") return path;
  return new URL(path, window.location.origin).href;
}

function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    firebase.messaging.isSupported()
  );
}

async function registerMessagingServiceWorker() {
  const registration = await navigator.serviceWorker.register(SW_PATH);
  await navigator.serviceWorker.ready;
  return registration;
}

function getNotificationTarget(data = {}) {
  const type = data.type || "";

  switch (type) {
    case "user_rewards": {
      const userRewardId = data.userRewardId || "";
      if (userRewardId) {
        return `${paths.REWARD_DETAIL}?userRewardId=${encodeURIComponent(userRewardId)}`;
      }
      return paths.REWARD_PANEL;
    }
    case "level_up":
    case "tier_up":
      return paths.LEVEL_JOURNEY;
    case "badge_earned":
      return `${paths.BADGES_PANEL}?tab=earned`;
    default:
      return paths.HOME_SCREEN;
  }
}

function buildNotificationPayload(payload) {
  const title =
    payload.notification?.title || payload.data?.title || "Dreamer Designs";
  const body = payload.notification?.body || payload.data?.body || "";
  const data = payload.data || {};

  return {
    title,
    options: {
      body,
      icon: NOTIFICATION_ICON,
      data,
      tag: data.type || "gemlogs-notification",
    },
  };
}

async function showForegroundNotification(payload) {
  const { title, options } = buildNotificationPayload(payload);

  console.log("[FCM] Foreground message received:", {
    title,
    body: options.body,
    type: options.data?.type,
    permission: Notification.permission,
  });

  if (Notification.permission !== "granted") {
    console.warn(
      "[FCM] Notification permission is not granted:",
      Notification.permission,
    );
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, options);
    console.log("[FCM] Foreground notification displayed via service worker");
    return;
  } catch (swError) {
    console.warn(
      "[FCM] Service worker showNotification failed, falling back:",
      swError,
    );
  }

  try {
    const notification = new Notification(title, options);
    notification.onclick = () => {
      window.focus();
      window.location.assign(resolveNotificationUrl(getNotificationTarget(options.data)));
      notification.close();
    };
    console.log("[FCM] Foreground notification displayed via Notification API");
  } catch (error) {
    console.error("[FCM] Failed to show foreground notification:", error);
  }
}

let foregroundListenerAttached = false;

function handleServiceWorkerNotificationClick(event) {
  if (event.data?.type !== "FCM_NOTIFICATION_CLICK" || !event.data?.url) return;
  window.focus();
  window.location.assign(event.data.url);
}

export function subscribeToForegroundMessages() {
  if (!isPushSupported()) {
    console.warn("[FCM] Foreground listener skipped — push not supported.");
    return;
  }

  if (foregroundListenerAttached) {
    console.log("[FCM] Foreground listener already attached.");
    return;
  }

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", handleServiceWorkerNotificationClick);
  }

  const messaging = firebase.messaging();
  messaging.onMessage((payload) => {
    showForegroundNotification(payload);
  });
  foregroundListenerAttached = true;
  console.log("[FCM] Foreground message listener attached.");
}

export async function requestWebFcmToken() {
  if (!isPushSupported()) {
    console.warn("[FCM] Push notifications are not supported in this browser.");
    return null;
  }

  if (!firebaseVapidKey) {
    console.warn(
      "[FCM] Missing REACT_APP_FIREBASE_VAPID_KEY. Add your Web Push certificate key from Firebase Console.",
    );
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.warn("[FCM] Notification permission not granted:", permission);
      return null;
    }

    const registration = await registerMessagingServiceWorker();
    const messaging = firebase.messaging();
    const token = await messaging.getToken({
      vapidKey: firebaseVapidKey,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
      console.log(
        "[FCM] Web token generated:",
        `${token.slice(0, 10)}...${token.slice(-8)}`,
      );
    }

    return token || null;
  } catch (error) {
    console.error("[FCM] Failed to get web FCM token:", error);
    return null;
  }
}

export function getStoredFcmToken() {
  return localStorage.getItem(FCM_TOKEN_STORAGE_KEY) || "";
}

export function clearStoredFcmToken() {
  localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
}
